import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import ColumnTable from '../../../shared/ColumnTable'
import { formatParcelLogs } from '../../../utils'

/**
 * Everything that happened to a parcel, in one table, oldest first.
 *
 * It used to be four tables — reallocations, user logs, driver logs and other logs — each sorted on
 * its own, so following a parcel through an incident meant reading four lists side by side and
 * merging them by eye. The reallocation columns (box, code, reason) are folded into the log text,
 * since they only ever applied to one of the four kinds and left the other three with empty cells.
 */

const transformLogString = (log, t) => {
  // iAdmin actions are stored as subType SUCCESS with the name in data.action, since service-core
  // rejects any other subType; entries written before that carry the name in subType
  const action =
    log.type === 'ACTION' ? log.data?.action ?? log.subType : undefined
  if (action === 'SMS') {
    return t('trailSmsSent', {
      phone: log.data?.phone,
    })
  }
  if (log.type === 'CHANGE') {
    return t('trailValueChanged', {
      field: t(log.subType?.toLowerCase?.()) ?? '',
      oldValue: log.data?.previousValue,
      newValue: log.data?.newValue,
    })
  }
  if (action === 'SUPPORT_NOTE') {
    return t('trailSupportNote')
  }
  if (action === 'EMAIL') {
    return t('trailEmail', { template: log.data?.template })
  }
  if (action === 'RESET_PHONE_EMAIL') {
    return t('trailResetPhoneEmail')
  }
  if (action === 'RESET_CONSUMER_ACCESS') {
    return t('trailConsumerAccessReset')
  }
  if (action === 'FORCE_STATUS') {
    return t('trailForceStatus', {
      from: t(`statuses.${log.data?.from}`),
      to: t(`statuses.${log.data?.to}`),
    })
  }
  if (action === 'CHANGE_PREVIOUS_BOX') {
    return t('trailPreviousBox')
  }
  if (action === 'BANKID_OVERRIDE') {
    return log.data?.enabled ? t('trailBankIdOff') : t('trailBankIdOn')
  }
  if (action === 'CLEAR_SIGNATURES') {
    return t('trailClearSignatures')
  }
  if (action === 'CONSUMER_DATA') {
    return t('trailConsumerData', {
      fields: (log.data?.changedFields || []).join(', '),
    })
  }
  if (action === 'CHANGE_LOCATION') {
    return t('trailLocationChanged')
  }
  if (log.type === 'COMPARTMENT_CHANGE') {
    return t('trailCompartmentChanged', {
      compartment: t(`boxTypes.${log.data?.compartment}`),
    })
  }
  return ''
}

const reallocationText = ({ fromLabel, toLabel, toBox, data }) => {
  const to =
    toLabel ??
    (toBox && typeof toBox.port === 'number' ? `${toBox.port + 1}` : '')
  const movement = [fromLabel, to].filter(Boolean).join(' → ')
  // Box, code and reason were columns of their own; as text they travel with the row. The comment
  // comes with them: `CustomReason` is a code meaning "free text", so on its own it says nothing —
  // what the driver typed beside it is the only thing that explains the move.
  const extras = [data?.code, data?.reason, data?.comment]
    .filter(Boolean)
    .join(' · ')
  return [movement, extras].filter(Boolean).join(' | ')
}

/** The four sources, each flattened to the same shape so one sort covers all of them. */
const collect = (parcel, driverLogs, serviceLogs, describeUserLog) => {
  const rows = []

  ;(parcel.reallocations || []).forEach((reallocation) => {
    rows.push({
      created: reallocation.created,
      kind: 'reallocation',
      text: reallocationText(reallocation),
      who: reallocation.driver,
    })
  })
  ;(parcel.auditTrail || [])
    .filter(
      (entry) =>
        entry?.serviceCallerChain?.includes('iadmin') &&
        !(entry?.type === 'CHANGE' && entry?.subType === 'STATUS')
    )
    .forEach((entry) => {
      rows.push({
        // service-core names this `timestamp` and rewrites an entry it finds a `created` on, so
        // both shapes are out there: entries it has saved since, and ones it has not touched yet
        created: entry.timestamp ?? entry.created,
        kind: 'user',
        text: describeUserLog(entry),
        who: entry.username,
      })
    })

  // formatParcelLogs also emits a reallocation entry into both lists, for the reallocations that
  // happen to have a driver log beside them. parcel.reallocations above already has all of them.
  const notAReallocation = (log) => log.logType !== 'reallocation'

  driverLogs.filter(notAReallocation).forEach((log) => {
    rows.push({
      created: log.created,
      kind: 'driver',
      text: log.log,
      who: log.driver,
    })
  })

  serviceLogs.filter(notAReallocation).forEach((log) => {
    rows.push({
      created: log.created,
      // a consumer log is not a service log, and support reads them for different reasons
      // formatParcelLogs has already read a carrier-performed collection as a consumer log
      kind: log.logType === 'consumer' ? 'consumer' : 'service',
      text: log.log,
      who: log.driver,
    })
  })

  // several logs share a timestamp, so a row's key is what it says; a log repeated word for
  // word still needs telling apart
  const seen = new Map()
  const keyed = rows.map((row) => {
    const said = `${row.created}|${row.kind}|${row.text}|${row.who || ''}`
    const count = seen.get(said) ?? 0
    seen.set(said, count + 1)
    return { ...row, key: count ? `${said}|${count}` : said }
  })

  // Oldest first, the same way the table is sorted by default: clicking the
  // active sort direction clears the sort, and then this order is what shows.
  return keyed.sort(
    (a, b) => new Date(a.created || 0) - new Date(b.created || 0)
  )
}

// What a column sorts by is what the user reads in its cells, so the kind is compared by its
// label and not by its key (in Swedish the two orders have nothing to do with each other).
const compare = (orderBy, valueOf) => (a, b) =>
  orderBy === 'created'
    ? new Date(a.created || 0) - new Date(b.created || 0)
    : String(valueOf(a, orderBy) ?? '').localeCompare(
        String(valueOf(b, orderBy) ?? '')
      )

// Two of the four sources are read from the server, so the table appears with
// what the parcel carries and fills in when they arrive. With nothing in any
// of them there is no card.
export default function Logs({ parcel }) {
  const { t } = useTranslation('parcels')
  const dispatch = useDispatch()
  const [driverLogs, setDriverLogs] = useState([])
  const [serviceLogs, setServiceLogs] = useState([])
  const [loading, setLoading] = useState(true)
  // Oldest first: the table is read as the story of the parcel, so it runs the
  // way the parcel went rather than newest-first like a feed.
  const [sorting, setSorting] = useState({ orderBy: 'created', order: 'asc' })

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    // a failed read leaves the other sources standing
    // formatParcelLogs reads the box list (a request customer care is refused) only to phrase the
    // reallocations, and those rows are dropped below: parcel.reallocations already has them all
    const withoutReallocations = { ...parcel, reallocations: [] }
    const readAll = (...args) =>
      formatParcelLogs(withoutReallocations, dispatch, t, ...args).catch(
        () => []
      )
    Promise.all([readAll(), readAll('service|consumer|carrier')]).then(
      ([driver, service]) => {
        if (cancelled) return
        setDriverLogs(driver)
        setServiceLogs(service)
        setLoading(false)
      }
    )
    return () => {
      cancelled = true
    }
  }, [dispatch, parcel, t])

  const rows = useMemo(
    () =>
      collect(parcel, driverLogs, serviceLogs, (log) =>
        transformLogString(log, t)
      ),
    [parcel, driverLogs, serviceLogs, t]
  )

  const kindLabel = useCallback(
    (row) => t(`logKinds.${row.kind}`, { defaultValue: row.kind }),
    [t]
  )

  const sortedRows = useMemo(() => {
    if (!sorting.orderBy) return rows
    const sign = sorting.order === 'asc' ? 1 : -1
    const valueOf = (row, key) => (key === 'kind' ? kindLabel(row) : row[key])
    return [...rows].sort(
      (a, b) => sign * compare(sorting.orderBy, valueOf)(a, b)
    )
  }, [rows, sorting, kindLabel])

  const columns = useMemo(
    () => [
      {
        id: 'created',
        label: t('time'),
        width: 180,
        sortable: true,
        render: (row) => (row.created ? window.niceDate2(row.created) : ''),
      },
      {
        id: 'kind',
        label: t('logKind'),
        width: 140,
        sortable: true,
        render: kindLabel,
      },
      { id: 'text', label: t('log'), sortable: true },
      {
        id: 'who',
        label: t('user'),
        width: 180,
        sortable: true,
        render: (row) => row.who || '',
      },
    ],
    [t, kindLabel]
  )

  if (!rows.length) return null

  return (
    <Stack spacing={1}>
      {/* no heading of its own: the box around this part is titled boxes.logs,
          and the two keys translate to the same word in all three languages */}
      <ColumnTable
        storageKey="parcelLogs"
        columns={columns}
        rows={sortedRows}
        getRowKey={(row) => row.key}
        sorting={sorting}
        onSort={setSorting}
        loading={loading}
      />
    </Stack>
  )
}
